import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import moment from "moment";

import ConfirmationPopup from "../../../../packages/ConfirmationPopup";
import CustomModal from "../../../../packages/CustomModal";
import { useToastNotification } from "../../../../packages/WPToastNotification";

import useTimezone from "../../../../hooks/useTimezone";

import { AssignLicenseFields } from "../../AssignLicenseFields";

import {
  CustomModalButtons,
  DATE_FORMATS,
} from "../../../../constants/global.constants";
import {
  HIDDEN_LICENSE_IDS,
  LICENSE_STATUS,
} from "../../../../constants/LicenseManagement.constants";

import { terminateUsersLicense } from "../../../../network/LicenseApis";

const TerminateLicense = ({
  userData,
  onCloseModal,
  onUserLicenseUpdate,
  selectedLicenseId,
}) => {
  const { t } = useTranslation();
  const { formatTime } = useTimezone();
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();

  const { name = "", email = "" } = userData || {};

  const [licenseForm, setLicenseForm] = useState({
    selectedLicenseName: null,
  });
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showTerminateConfirm, setShowTerminateConfirm] = useState(false);

  const { selectedLicenseName } = licenseForm;

  /**
   * Pre-select the correct license in the dropdown when the modal opens for terminate.
   * If selectedLicenseId is provided, select that specific license (from view user details).
   * If no selectedLicenseId, select the first valid license ( not public default && not expired && not terminated) in the list (from 3-dot menu).
   */
  useEffect(() => {
    if (
      licenseForm.selectedLicenseName ||
      !Array.isArray(userData?.licenses) ||
      userData.licenses.length === 0
    )
      return;

    const validLicenses = userData.licenses.filter(
      (license) =>
        !HIDDEN_LICENSE_IDS.includes(license.id) &&
        license.licenseStatus !== LICENSE_STATUS.expired &&
        license.licenseStatus !== LICENSE_STATUS.terminated,
    );

    const selectedLicense = selectedLicenseId
      ? validLicenses.find((license) => license.id === selectedLicenseId)
      : validLicenses[0];

    if (!selectedLicense) return;

    setLicenseForm((prev) => ({
      ...prev,
      selectedLicenseName: {
        label: {
          nameType: `${selectedLicense.licenseType || ""} ${selectedLicense.name || ""}`,
          expiry: formatTime(
            selectedLicense.expiryDate,
            true,
            DATE_FORMATS.MMM_DD_YYYY,
          ),
        },
        value: selectedLicense,
      },
    }));
  }, [
    selectedLicenseId,
    userData?.licenses,
    licenseForm.selectedLicenseName,
    formatTime,
  ]);

  const handleLicenseAction = async () => {
    if (!selectedLicenseName) return;
    const { value: licenseObj = {} } = selectedLicenseName;
    const { userId, email } = userData || {};
    const { id, name, assignedDate, licenseType } = licenseObj;
    const expiryDateValue = moment()
      .subtract(1, "day")
      .format(DATE_FORMATS.YYYY_MM_DD);
    const licenseApproverValue = licenseObj.licenseApprover;
    const licenseStatuses = LICENSE_STATUS.terminated;

    const licenseActionPayload = [
      {
        userId,
        email,
        licenses: [
          {
            id,
            name,
            assignedDate,
            expiryDate: expiryDateValue,
            licenseType,
            licenseApprover: licenseApproverValue,
          },
        ],
      },
    ];
    const loadingMsgKey = "licenseManagement.terminatingLicense";
    const successMsgKey = "licenseManagement.licenseSuccessfullyTerminated";
    const errorMsgKey = "licenseManagement.licenseTerminationFailed";

    setLoadingMsg(t(loadingMsgKey));
    try {
      await terminateUsersLicense(licenseActionPayload);
      showSuccessNotification(t(successMsgKey));

      /** Update the local user license state after successful terminate API call */
      const updatedLicense = {
        id,
        name,
        assignedDate,
        expiryDate: expiryDateValue,
        licenseType,
        licenseApprover: licenseApproverValue,
        licenseStatus: licenseStatuses,
      };
      onUserLicenseUpdate?.(userId, updatedLicense);
      onCloseModal();
    } catch {
      showErrorNotification(t(errorMsgKey));
    } finally {
      setLoadingMsg("");
    }
  };

  const handleConfirmBtnClick = ({ target }) => {
    if (target?.id !== CustomModalButtons.CONFIRM) return onCloseModal();
    setShowTerminateConfirm(true);
  };

  const handleTerminateConfirm = (id) => {
    setShowTerminateConfirm(false);
    if (id === CustomModalButtons.CONFIRM) handleLicenseAction();
  };

  const isLicenseBtnDisabled = !selectedLicenseName;

  return (
    <>
      {!showTerminateConfirm && (
        <CustomModal
          customclass="terminate-license-modal"
          header={name}
          headerHelper={email}
          confirmBtn={t("licenseManagement.terminateLicense")}
          onModalClose={handleConfirmBtnClick}
          width="684px"
          confirmBtnDisabled={isLicenseBtnDisabled}
          profileInitials={name?.charAt(0)?.toUpperCase()}
          isLoading={Boolean(loadingMsg)}
          loadingText={loadingMsg}
          headerDataTestId="terminate_license"
          confirmBtnType="danger"
        >
          <AssignLicenseFields
            licenseForm={licenseForm}
            setLicenseForm={setLicenseForm}
            userData={userData}
            dataTestId="assign_license_date"
          />
        </CustomModal>
      )}
      {showTerminateConfirm && (
        <ConfirmationPopup
          header={t("licenseManagement.terminateLicense")}
          description={t("licenseManagement.terminateDescription")}
          type="danger"
          confirmBtn={t("licenseManagement.terminateButton")}
          cancelBtn={t("ValueStrings.cancel")}
          onClose={handleTerminateConfirm}
        />
      )}
    </>
  );
};

export default TerminateLicense;
